import { useCallback, useEffect, useState } from "react";
import { isLocalDashboard } from "./api";

const STORAGE_KEY = "ou-env";
const DEFAULT_ENV = "prod"; // Built primarily for prod usage.

/**
 * `local` is only valid when the dashboard itself is served from localhost.
 * On the deployed admin app we never expose it — a stored "local" value from
 * a previous localhost session falls back to prod.
 */
const isValid = (v) =>
  v === "prod" || v === "dev" || (v === "local" && isLocalDashboard());

/**
 * Persists the selected environment in localStorage so it survives reloads
 * and is shared across Index → Detail → Profile drill-downs.
 *
 * Multiple `useEnv` instances stay in sync via the `storage` event so
 * switching env on one page updates any open siblings.
 */
export function useEnv() {
  const [env, setEnvState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_ENV;
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return isValid(stored) ? stored : DEFAULT_ENV;
    } catch {
      return DEFAULT_ENV;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && isValid(e.newValue)) {
        setEnvState(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setEnv = useCallback((next) => {
    if (!isValid(next)) return;
    setEnvState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore quota / private-mode errors
    }
  }, []);

  return [env, setEnv];
}
